import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProgram } from '@/api/entities';
import { deleteCampSession, deleteUserCampSession } from '@/services/campHierarchyService';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Calendar, DollarSign, Clock, CheckCircle, Check } from 'lucide-react';
import { formatCampDisplayName } from '@/utils/campNameFormatter';
import { format } from 'date-fns';
import { getCurrentUser, supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

/**
 * SessionList component for displaying and managing multiple sessions for a camp
 * 
 * @param {Object} props
 * @param {string} props.baseCampId - ID of the base camp
 * @param {Array} props.sessions - Array of session records
 * @param {Function} props.onAddSession - Callback when "Add Session" is clicked
 * @param {Function} props.onEditSession - Callback when a session is edited: (sessionId) => void
 * @param {Function} props.onDeleteSession - Callback when a session is deleted: (sessionId) => void
 * @param {Function} props.onRefresh - Callback to refresh the session list
 * @param {Function} [props.onSessionClick] - Optional callback when session card is clicked: (sessionId) => void
 */
const SessionList = ({ 
  baseCampId, 
  sessions = [], 
  onAddSession, 
  onEditSession, 
  onDeleteSession,
  onRefresh,
  onSessionClick
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(null);
  const [isSavingStatus, setIsSavingStatus] = useState(null);

  // Helper functions to check session status
  const getSessionStatus = (session) => {
    const status = session.status || session.program_data?.status;
    if (!status) return [];
    return Array.isArray(status) ? status : [status].filter(Boolean);
  };

  const hasStatus = (session, statusToCheck) => {
    const statuses = getSessionStatus(session);
    return statuses.some(s => String(s).toLowerCase() === statusToCheck.toLowerCase());
  };

  const handleDelete = async (session) => {
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }
    const sessionId = session?.id;
    if (!sessionId) return;

    setIsDeleting(sessionId);
    try {
      if (session.from_user_camp_sessions) {
        // Delete from user_camp_sessions table (optimized user-specific sessions)
        await deleteUserCampSession(sessionId);
      } else if (session.from_camp_audit_data) {
        // Delete from camp_audit_data table (community sessions)
        await deleteCampSession(sessionId);
      } else {
        // Fallback: delete from user_programs table (legacy sessions)
        await UserProgram.delete(sessionId);
      }
      if (onDeleteSession) onDeleteSession(sessionId);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Failed to delete session. Please try again.');
    } finally {
      setIsDeleting(null);
    }
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'No dates';
    try {
      // Parse dates as local dates to avoid timezone conversion issues
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        
        // If it's already a Date object, return it
        if (dateStr instanceof Date) return dateStr;
        
        // If it's an ISO date string (YYYY-MM-DD), parse it as local date
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
        }
        
        // Fallback to regular Date parsing
        return new Date(dateStr);
      };
      
      const start = parseDate(startDate);
      const end = parseDate(endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Invalid dates';
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } catch (error) {
      return 'Invalid dates';
    }
  };

  // PHASE 3: Handle session click - navigate to session detail page
  const handleSessionClick = (session) => {
    if (onSessionClick) {
      onSessionClick(session.id, session);
      return;
    }

    // Default navigation behavior
    const sessionId = session.id;
    if (!sessionId) return;

    if (session?.from_user_camp_sessions) {
      // Navigate to parent camp for user_camp_sessions
      const parentCampId = session.parent_camp_id || baseCampId;
      if (parentCampId) {
        navigate(`/program/${parentCampId}?mode=user_program`);
      }
    } else if (session?.from_camp_audit_data) {
      // Navigate to session detail page
      navigate(`/program/${sessionId}`);
    } else {
      // Navigate to user_program mode
      navigate(`/program/${sessionId}?mode=user_program`);
    }
  };

  // PHASE 3: Handle quick action (Consider/Book) with auto-save
  const handleQuickAction = async (e, session, action) => {
    e.stopPropagation(); // Prevent navigation when clicking action button
    
    const sessionId = session.id;
    if (!sessionId) return;

    setIsSavingStatus(sessionId);
    
    try {
      const user = await getCurrentUser();
      if (!user) {
        toast({
          title: 'Authentication required',
          description: 'Please log in to update session status',
          variant: 'destructive',
        });
        return;
      }

      // Check if user_programs entry exists for this session
      // Try multiple ways to find existing entry:
      // 1. By ID (if session.id is a user_programs ID)
      // 2. By place_id in program_data (if session has place_id)
      // 3. By parent_camp_id + session name (for sessions)
      const sessionData = session.program_data || {};
      const sessionPlaceId = session.place_id || sessionData.place_id;
      const sessionName = session.session || sessionData.session;
      
      let existing = null;
      
      // First try by ID
      if (sessionId) {
        const { data: byId } = await supabase
          .from('user_programs')
          .select('id, status, program_data')
          .eq('user_id', user.id)
          .eq('id', sessionId)
          .maybeSingle();
        if (byId) existing = byId;
      }
      
      // If not found, try by parent_camp_id + session name or place_id
      // Get all sessions for this parent camp and find the matching one
      if (!existing && baseCampId) {
        const { data: allSessions } = await supabase
          .from('user_programs')
          .select('id, status, program_data')
          .eq('user_id', user.id)
          .eq('parent_camp_id', baseCampId);
        
        if (allSessions && allSessions.length > 0) {
          // Find matching session by place_id or session name
          for (const sessionEntry of allSessions) {
            const pd = typeof sessionEntry.program_data === 'string' 
              ? JSON.parse(sessionEntry.program_data) 
              : sessionEntry.program_data;
            
            // Match by place_id if available
            if (sessionPlaceId && pd?.place_id === sessionPlaceId) {
              existing = sessionEntry;
              break;
            }
            
            // Match by session name if available
            if (sessionName && pd?.session === sessionName) {
              existing = sessionEntry;
              break;
            }
          }
        }
      }

      if (existing) {
        // Update existing entry
        const currentStatuses = Array.isArray(existing.status) 
          ? existing.status 
          : [existing.status].filter(Boolean);
        
        // Add the new status if not already present
        const normalizedAction = String(action).toLowerCase();
        let newStatuses;
        if (!currentStatuses.some(s => String(s).toLowerCase() === normalizedAction)) {
          newStatuses = [...currentStatuses, normalizedAction];
        } else {
          // Status already exists, keep current statuses
          newStatuses = currentStatuses;
        }
        
        // Update with new statuses and ensure dates are preserved
        const existingPd = typeof existing.program_data === 'string'
          ? JSON.parse(existing.program_data)
          : existing.program_data || {};
        
        await UserProgram.update(existing.id, {
          status: newStatuses,
          // Preserve dates in program_data
          program_data: {
            ...existingPd,
            // Ensure dates are in both formats for compatibility
            user_camp_start_date: existingPd.user_camp_start_date || existingPd.camp_start_date || existingPd.start_date,
            user_camp_end_date: existingPd.user_camp_end_date || existingPd.camp_end_date || existingPd.end_date,
            camp_start_date: existingPd.user_camp_start_date || existingPd.camp_start_date || existingPd.start_date,
            camp_end_date: existingPd.user_camp_end_date || existingPd.camp_end_date || existingPd.end_date,
            start_date: existingPd.user_camp_start_date || existingPd.camp_start_date || existingPd.start_date,
            end_date: existingPd.user_camp_end_date || existingPd.camp_end_date || existingPd.end_date,
          },
        });
      } else {
        // Create new user_programs entry for this session
        const sessionData = session.program_data || {};
        const { data: profileData } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', user.id)
          .maybeSingle();

        const userEmail = profileData?.email || user.email;
        
        // Get session dates - prioritize user_camp_start_date/user_camp_end_date
        const startDate = session.user_camp_start_date || 
                         sessionData.user_camp_start_date || 
                         session.start_date || 
                         session.camp_start_date ||
                         sessionData.start_date ||
                         sessionData.camp_start_date;
        const endDate = session.user_camp_end_date || 
                       sessionData.user_camp_end_date || 
                       session.end_date || 
                       session.camp_end_date ||
                       sessionData.end_date ||
                       sessionData.camp_end_date;

        await UserProgram.create({
          user_email: userEmail,
          program_type: 'camp',
          program_data: {
            name: session.name || sessionData.name,
            organization: session.organization || sessionData.organization,
            location: session.location || sessionData.location,
            place_id: session.place_id || session.id,
            session: session.session || sessionData.session,
            session_year: session.session_year || sessionData.session_year,
            // Store dates in both formats for compatibility
            user_camp_start_date: startDate,
            user_camp_end_date: endDate,
            camp_start_date: startDate,
            camp_end_date: endDate,
            start_date: startDate,
            end_date: endDate,
            cost: session.cost || sessionData.cost || sessionData.price,
            ...sessionData,
          },
          status: [action],
          child_names: session.child_names || [],
          parent_camp_id: baseCampId,
          year_tracked: new Date().getFullYear(),
        });
      }

      toast({
        title: 'Status updated',
        description: `Session marked as ${action}.`,
      });

      // Refresh session list
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error updating session status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update session status. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingStatus(null);
    }
  };

  if (!baseCampId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">
          Sessions ({sessions.length})
        </h4>
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onAddSession) {
              onAddSession();
            }
          }}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Session
        </Button>
      </div>

      {sessions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-4">
              No sessions added yet. Add a session to track specific camp dates.
            </p>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onAddSession) {
                  onAddSession();
                }
              }}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Add First Session
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sessions.map((session) => {
            const sessionData = session.program_data || {};
            
            // For sessions, show only the session name, not "Camp Name - Session Name"
            const sessionName = session.session || 
                              sessionData.session || 
                              formatCampDisplayName(session);
            
            // ✅ Read from correct database field names with fallbacks for backwards compatibility
            const startDate = session.user_camp_start_date || 
                            sessionData.user_camp_start_date ||
                            session.start_date || 
                            session.camp_start_date || 
                            sessionData.camp_start_date || 
                            sessionData.start_date;
            
            const endDate = session.user_camp_end_date || 
                          sessionData.user_camp_end_date ||
                          session.end_date || 
                          session.camp_end_date || 
                          sessionData.camp_end_date || 
                          sessionData.end_date;

            return (
              <Card 
                key={session.id} 
                className="border cursor-pointer hover:border-purple-300 hover:shadow-md transition-all"
                onClick={() => handleSessionClick(session)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-2">
                        <h5 className="font-semibold text-gray-900 line-clamp-2 break-words">
                          {sessionName}
                        </h5>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {startDate && endDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDateRange(startDate, endDate)}
                          </span>
                        )}
                        {(session.session_year || sessionData.session_year) && (
                          <span className="text-xs text-gray-500">
                            Year: {session.session_year || sessionData.session_year}
                          </span>
                        )}
                        {(session.cost ?? sessionData.cost ?? sessionData.price ?? session.session_price) && (
                          <span className="flex items-center gap-1 text-xs font-medium text-gray-700">
                            <DollarSign className="w-3 h-3" />
                            {parseFloat(session.cost ?? sessionData.cost ?? sessionData.price ?? session.session_price ?? 0).toFixed(2)}
                          </span>
                        )}
                        {(session.grade_range || sessionData.grade_range) && (
                          <span className="text-xs text-gray-600">
                            Grades: {session.grade_range || sessionData.grade_range}
                          </span>
                        )}
                      </div>
                      {session.child_names && session.child_names.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          Children: {Array.isArray(session.child_names) ? session.child_names.join(', ') : session.child_names}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {/* Status badges - show if session has status */}
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {hasStatus(session, 'considering') && (
                          <Badge 
                            variant="outline" 
                            className="bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100"
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            Considering
                          </Badge>
                        )}
                        {hasStatus(session, 'booked') && (
                          <Badge 
                            variant="outline" 
                            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Booked
                          </Badge>
                        )}
                      </div>
                      
                      {/* Action buttons - Edit and Delete only */}
                      <div className="flex items-center gap-1">
                        {/* Edit button - icon only */}
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSession && onEditSession(session.id, session);
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label="Edit session"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {/* Delete button - icon only */}
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(session);
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={isDeleting === session.id}
                          aria-label={isDeleting === session.id ? 'Deleting...' : 'Delete session'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionList;
